import React from "react";
import { useTranslation } from "react-i18next";

import {
  Box,
  Button,
  CompassColor,
  Heading,
  NeoColor,
  Spacing,
  Stack,
  Text,
} from "@noom/wax-component-library";

import {
  QuestionComponentProps,
  QuestionDefinition,
} from "@/components/survey/Question";
import { BaseInterstitial } from "@/components/survey/questionTypes/core";
import { useGoto, useTrackEvent } from "@/hooks";
import {
  failedAccessQualification,
  hasAccessExclusion,
} from "@/utils/cmsAccess";
import { isCmsAccessEnabled } from "@/utils/partners";

const QUESTION_ID = "accessIneligible";

// Same light sky background as the Connect Health Records screens.
const BACKGROUND_COLOR = NeoColor.blue.skyLight;

const AccessIneligible: React.FC<QuestionComponentProps> = ({
  onClickBack,
  onClickNext,
}) => {
  const { t } = useTranslation();
  const { trackAnonEvent } = useTrackEvent();
  const goto = useGoto();

  // The member leaves B2B enrollment for the regular B2C onboarding. Wait for
  // the click event first, because leaving the page cancels pending requests.
  const handleClickContinue = async () => {
    await trackAnonEvent("b2bEnrollmentAnonButtonClicked", {
      buttonName: "continueWithoutAccess",
      pageId: QUESTION_ID,
    });
    goto.b2cOnboarding();
  };

  return (
    <BaseInterstitial
      backgroundColor={BACKGROUND_COLOR}
      color={CompassColor.black}
      onClickBack={onClickBack}
      onClickNext={onClickNext}
      questionId={QUESTION_ID}
    >
      <Stack paddingTop="var(--spacing-xxl)" spacing={Spacing[4]}>
        <Heading>{t("accessIneligible.title")}</Heading>
        <Text>{t("accessIneligible.message")}</Text>
        <Text as="b">{t("accessIneligible.alternative")}</Text>
      </Stack>
      {/*
        On small screens: full-width bar pinned to the bottom; the button keeps
        the 400px column. The wrapper reserves the bar's height so it never
        covers the text on short screens (same approach as CheckboxQuestion's
        sticky CTA). On desktop the button sits right under the text instead.
      */}
      <Box height={{ base: "var(--spacing-xxxl)", lg: "auto" }}>
        <Box
          backgroundColor={BACKGROUND_COLOR}
          bottom={0}
          left={0}
          padding={{ base: "var(--spacing-m)", lg: "0" }}
          position={{ base: "fixed", lg: "static" }}
          right={0}
        >
          <Box margin="0 auto" maxWidth="400px">
            <Button
              colorScheme="primary"
              onClick={handleClickContinue}
              size="xl"
              w="100%"
            >
              {t("accessIneligible.ctaText")}
            </Button>
          </Box>
        </Box>
      </Box>
    </BaseInterstitial>
  );
};

export const Q_ACCESS_INELIGIBLE: QuestionDefinition = {
  id: QUESTION_ID,
  shouldShowQuestion: ({ partnerInfo, surveyAnswers }) =>
    isCmsAccessEnabled(partnerInfo) &&
    (failedAccessQualification(surveyAnswers.cmsAccessQualifyingConditions) ||
      hasAccessExclusion(surveyAnswers.cmsAccessDisqualifyingConditions)),
  component: AccessIneligible,
};
